import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { tvApi, useTVAuth } from './TVContext';
import { focusFirst, useDpadNavigation } from './focus';

const TVBrowse = () => {
  const navigate = useNavigate();
  const { user, signout } = useTVAuth();
  const [allContent, setAllContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useDpadNavigation(() => navigate('/tv'));

  useEffect(() => {
    const load = async () => {
      try {
        const res = await tvApi.get('/content', { params: { limit: 100 } });
        setAllContent(res.data || []);
      } catch (e) {
        setAllContent([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => { if (!loading) focusFirst(); }, [loading]);

  const rows = useMemo(() => {
    const featured = allContent.filter((c) => c.featured).sort((a, b) => (a.featured_order || 0) - (b.featured_order || 0));
    const movies = allContent.filter((c) => c.content_type === 'movie');
    const tv = allContent.filter((c) => c.content_type === 'tv_show');
    const music = allContent.filter((c) => c.content_type === 'music_video');
    const trailers = allContent.filter((c) => c.trailer_playback_id);
    return { featured, movies, tv, music, trailers };
  }, [allContent]);

  const heroItem = rows.featured[0] || allContent[0];

  if (loading) return <div className="tv-root tv-loading" data-testid="tv-browse-loading">Loading Pulse Media…</div>;

  return (
    <div className="tv-root" data-testid="tv-browse">
      <div className="tv-header">
        <div className="tv-brand">PULSE MEDIA</div>
        <div className="tv-user" data-testid="tv-user-info">
          {user ? (
            <>
              {user.avatar ? <img src={user.avatar} alt={user.username} /> : null}
              <span>{user.username}</span>
              {user.is_premium && <span style={{ color: '#1FA2FF', marginLeft: 6 }}>• Premium</span>}
              <button
                className="tv-btn tv-btn-ghost tv-focusable"
                style={{ padding: '8px 16px', fontSize: 14, marginLeft: 16 }}
                data-testid="tv-signout-btn"
                onClick={async () => { await signout(); navigate('/tv'); }}
              >
                Sign Out
              </button>
            </>
          ) : (
            <button
              className="tv-btn tv-focusable"
              style={{ padding: '10px 20px', fontSize: 15 }}
              data-testid="tv-signin-btn"
              onClick={() => navigate('/tv')}
            >
              Sign In
            </button>
          )}
        </div>
      </div>

      {heroItem && <FeaturedHero item={heroItem} onPlay={() => navigate(`/tv/content/${heroItem.id}`)} />}

      <TVRow title="Featured" items={rows.featured} onSelect={(id) => navigate(`/tv/content/${id}`)} testIdPrefix="featured" />
      <TVRow title="Movies" items={rows.movies} onSelect={(id) => navigate(`/tv/content/${id}`)} testIdPrefix="movies" />
      <TVRow title="TV Shows" items={rows.tv} onSelect={(id) => navigate(`/tv/content/${id}`)} testIdPrefix="tv-shows" />
      <TVRow title="Videos (Free)" items={rows.music} onSelect={(id) => navigate(`/tv/content/${id}`)} testIdPrefix="music" />
      <TVRow title="Trailers" items={rows.trailers} onSelect={(id) => navigate(`/tv/content/${id}?trailer=1`)} testIdPrefix="trailers" />
      <div style={{ height: 80 }} />
    </div>
  );
};

const FeaturedHero = ({ item, onPlay }) => {
  const title = item.title || item.name;
  const poster = item.thumbnail || item.poster;
  return (
    <div className="tv-featured" data-testid="tv-hero">
      {poster && <img src={poster} alt={title} />}
      <div className="tv-featured-overlay">
        <div className="tv-featured-title">{title}</div>
        <div className="tv-featured-desc">{item.description}</div>
        <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
          <button
            className="tv-btn tv-focusable"
            data-testid="tv-hero-play-btn"
            onClick={onPlay}
            autoFocus
          >
            ▶ View Details
          </button>
        </div>
      </div>
    </div>
  );
};

const TVRow = ({ title, items, onSelect, testIdPrefix }) => {
  if (!items || items.length === 0) return null;
  return (
    <section>
      <h3 className="tv-row-title">{title}</h3>
      <div className="tv-row" data-testid={`tv-row-${testIdPrefix}`}>
        {items.map((item) => (
          <TVCard key={item.id} item={item} onClick={() => onSelect(item.id)} testId={`tv-card-${testIdPrefix}-${item.id}`} />
        ))}
      </div>
    </section>
  );
};

const TVCard = ({ item, onClick, testId }) => {
  const poster = item.thumbnail || item.poster;
  const title = item.title || item.name;
  const locked = item.is_locked;
  const premium = item.is_subscription_required || item.is_premium;
  return (
    <button
      className="tv-card tv-focusable"
      onClick={onClick}
      data-testid={testId}
      aria-label={title}
    >
      {poster ? (
        <img src={poster} alt={title} style={locked ? { filter: 'blur(12px) brightness(0.6)' } : undefined} />
      ) : (
        <div style={{ width: '100%', height: '100%', background: '#0e131c' }} />
      )}
      {locked && <div className="tv-lock-badge">🔒 Coming Soon</div>}
      {!locked && premium && <div className="tv-premium-badge">PREMIUM</div>}
      <div className="tv-card-label">{title}</div>
    </button>
  );
};

export default TVBrowse;
